import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { validateDestinationUrl, sanitizeUrl } from '@qrqy/shared/src/urlValidator.js';
import { checkScannability } from '@qrqy/shared/src/qrConfig.js';
import QRCodeImage from '../components/QRCodeImage.jsx';

const COLORS = { black: '#0A0A0A', white: '#F5F5F0', gold: '#C9A84C', gray: '#888', surface: '#111111' };

// TODO(M3): Set VITE_API_BASE_URL to switch from mock data to real API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const REDIRECT_BASE_URL = import.meta.env.VITE_REDIRECT_BASE_URL || 'https://r.qrqy.shop';

// TODO(M3): Replace with real data from GET /api/qr-codes/:code
const MOCK_QR_DATA = {
  'XQ7R4P': {
    code: 'XQ7R4P',
    destination: 'https://open.spotify.com/album/12345',
    productName: 'QR TEE',
    scanCount: 31,
    isActive: true,
    createdAt: '2026-02-01T00:00:00Z',
  },
  'KN2M8A': {
    code: 'KN2M8A',
    destination: 'https://www.instagram.com/p/abcdef/',
    productName: 'QR HOODIE',
    scanCount: 16,
    isActive: true,
    createdAt: '2026-02-15T00:00:00Z',
  },
};


function ValidationMessage({ valid, errors }) {
  if (!errors || errors.length === 0) return null;
  return (
    <div style={{
      padding: '0.75rem',
      backgroundColor: valid ? 'rgba(80,200,120,0.06)' : 'rgba(224,92,92,0.06)',
      border: `1px solid ${valid ? 'rgba(80,200,120,0.25)' : 'rgba(224,92,92,0.25)'}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.3rem',
    }}>
      {errors.map((msg, i) => (
        <p key={i} style={{
          fontSize: '0.75rem',
          color: valid ? '#50c878' : '#e05c5c',
          margin: 0,
          lineHeight: 1.5,
        }}>
          {valid ? '✓ ' : '✗ '}{msg}
        </p>
      ))}
    </div>
  );
}

function ScannabilityWarnings({ warnings }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <div style={{
      padding: '0.75rem',
      backgroundColor: 'rgba(201,168,76,0.06)',
      border: `1px solid rgba(201,168,76,0.25)`,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.3rem',
    }}>
      {warnings.map((msg, i) => (
        <p key={i} style={{ fontSize: '0.75rem', color: COLORS.gold, margin: 0, lineHeight: 1.5 }}>
          ⚠ {msg}
        </p>
      ))}
    </div>
  );
}

export default function QREditor() {
  const { code } = useParams();
  const [qrData, setQrData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [analytics, setAnalytics] = useState(null);

  const [destinationInput, setDestinationInput] = useState('');
  const [validation, setValidation] = useState({ valid: false, errors: [] });
  const [scannability, setScannability] = useState({ scannable: true, warnings: [] });
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [saveError, setSaveError] = useState('');

  // Fetch QR data — real API when configured, mock fallback otherwise
  useEffect(() => {
    if (!API_BASE_URL) {
      setQrData(MOCK_QR_DATA[code] || null);
      return;
    }
    fetch(`${API_BASE_URL}/api/qr-codes/${code}`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then((data) => setQrData(data))
      .catch(() => {
        setLoadError('Failed to load QR code data.');
        setQrData(MOCK_QR_DATA[code] || null);
      });
  }, [code]);

  // Pre-fill input with current destination
  useEffect(() => {
    if (qrData) {
      setDestinationInput(qrData.destination || '');
    }
  }, [qrData]);

  // Fetch analytics — real API when configured
  useEffect(() => {
    if (!API_BASE_URL) return;
    fetch(`${API_BASE_URL}/api/qr-codes/${code}/analytics`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) setAnalytics(data); })
      .catch(() => {});
  }, [code]);

  // Real-time validation as user types
  const runValidation = useCallback((url) => {
    if (!url.trim()) {
      setValidation({ valid: false, errors: [] });
      setScannability({ scannable: true, warnings: [] });
      return;
    }

    const sanitized = sanitizeUrl(url);
    const validResult = validateDestinationUrl(sanitized);
    setValidation(validResult);

    if (validResult.valid) {
      const scanResult = checkScannability(sanitized);
      setScannability(scanResult);
    } else {
      setScannability({ scannable: true, warnings: [] });
    }
  }, []);

  function handleInputChange(e) {
    const val = e.target.value;
    setDestinationInput(val);
    runValidation(val);
    setSaveStatus('idle');
    setSaveError('');
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!validation.valid) return;

    setSaveStatus('saving');
    setSaveError('');

    if (!API_BASE_URL) {
      // No API configured — simulate success so the UI is testable in dev
      // TODO(M3): Remove once VITE_API_BASE_URL is set
      await new Promise((r) => setTimeout(r, 600));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
      return;
    }

    // Real API call — active when VITE_API_BASE_URL is set.
    // Server runs validateDestinationUrl + ownership.updateDestination from @qrqy/shared.
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr-codes/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destination: sanitizeUrl(destinationInput) }),
        credentials: 'include', // sends HttpOnly auth cookie
      });

      if (!res.ok) {
        if (res.status === 403) {
          setSaveError('You do not have permission to edit this QR code.');
        } else if (res.status === 401) {
          setSaveError('Your session has expired. Please sign in again.');
        } else {
          const data = await res.json().catch(() => ({}));
          setSaveError(data.error || 'Failed to update destination. Please try again.');
        }
        setSaveStatus('error');
        return;
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveError('Network error. Check your connection and try again.');
      setSaveStatus('error');
    }
  }

  if (!qrData && !loadError) {
    return <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: COLORS.gray, fontSize: '0.8rem', letterSpacing: '0.1em' }}>LOADING...</p>
    </div>;
  }

  if (!qrData) {
    return (
      <div style={{
        minHeight: '40vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
      }}>
        <p style={{ color: COLORS.gray, fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
          QR code not found.
        </p>
        <Link to="/my-qrs" style={{ fontSize: '0.72rem', color: COLORS.gold, textDecoration: 'none', letterSpacing: '0.1em' }}>
          ← BACK TO MY QRS
        </Link>
      </div>
    );
  }

  const hasChanged = destinationInput !== qrData.destination;
  const canSave = validation.valid && hasChanged && saveStatus !== 'saving';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
      {/* Inactive notice — shown when owner has deactivated this code */}
      {qrData && !qrData.isActive && (
        <div style={{
          padding: '1rem 1.5rem',
          backgroundColor: 'rgba(224,92,92,0.06)',
          border: '1px solid rgba(224,92,92,0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <span style={{ fontSize: '0.65rem', letterSpacing: '0.15em', color: '#e05c5c', fontWeight: 700 }}>
            INACTIVE
          </span>
          <p style={{ fontSize: '0.78rem', color: '#e05c5c', margin: 0 }}>
            This QR code is currently deactivated. Scans will show a "deactivated" notice
            instead of redirecting. Contact support to re-activate your piece.
          </p>
        </div>
      )}

      {/* Breadcrumb */}
      <nav>
        <Link to="/my-qrs" style={{ fontSize: '0.7rem', letterSpacing: '0.12em', color: COLORS.gray, textDecoration: 'none' }}>
          MY QRS
        </Link>
        <span style={{ color: COLORS.gray, margin: '0 0.75rem', opacity: 0.4 }}>→</span>
        <span style={{ fontSize: '0.7rem', letterSpacing: '0.12em', color: COLORS.white }}>{code}</span>
      </nav>

      {/* Two-column layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '280px 1fr',
        gap: '3rem',
        alignItems: 'start',
      }}>
        {/* Left: QR preview */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.25rem',
          padding: '2rem',
          border: `1px solid rgba(255,255,255,0.08)`,
          backgroundColor: COLORS.surface,
        }}>
          {/* Real QR code */}
          <QRCodeImage
            value={`${REDIRECT_BASE_URL}/r/${code}`}
            size={160}
          />

          <div style={{ textAlign: 'center' }}>
            <p style={{
              fontSize: '0.65rem',
              letterSpacing: '0.18em',
              color: COLORS.gold,
              margin: '0 0 0.25rem',
              fontWeight: 700,
            }}>
              {code}
            </p>
            <p style={{ fontSize: '0.7rem', color: COLORS.gray, margin: 0 }}>
              {qrData.productName}
            </p>
            <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', margin: '0.5rem 0 0' }}>
              Scan this
            </p>
          </div>

          <div style={{
            width: '100%',
            padding: '0.75rem',
            borderTop: `1px solid rgba(255,255,255,0.06)`,
            textAlign: 'center',
          }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: COLORS.white }}>
              {qrData.scanCount}
            </span>
            <p style={{ fontSize: '0.6rem', color: COLORS.gray, margin: '0.2rem 0 0', letterSpacing: '0.12em' }}>
              TOTAL SCANS
            </p>
          </div>
        </div>

        {/* Right: editor form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <div>
            <p style={{
              fontSize: '0.65rem',
              letterSpacing: '0.25em',
              color: COLORS.gold,
              margin: '0 0 0.5rem',
              fontWeight: 600,
            }}>
              DESTINATION EDITOR
            </p>
            <h1 style={{
              fontSize: 'clamp(1.25rem, 2.5vw, 2rem)',
              fontWeight: 900,
              letterSpacing: '-0.01em',
              color: COLORS.white,
              margin: 0,
            }}>
              {qrData.productName}
            </h1>
          </div>

          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Current destination */}
            <div>
              <p style={{ fontSize: '0.62rem', letterSpacing: '0.15em', color: COLORS.gray, margin: '0 0 0.5rem', fontWeight: 600 }}>
                CURRENT DESTINATION
              </p>
              <p style={{
                fontSize: '0.82rem',
                color: 'rgba(255,255,255,0.55)',
                fontFamily: 'monospace',
                padding: '0.75rem',
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: `1px solid rgba(255,255,255,0.06)`,
                wordBreak: 'break-all',
                margin: 0,
              }}>
                {qrData.destination || 'Not set'}
              </p>
            </div>

            {/* New destination input */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label
                htmlFor="destination-input"
                style={{ fontSize: '0.62rem', letterSpacing: '0.15em', color: COLORS.white, fontWeight: 700 }}
              >
                NEW DESTINATION
              </label>
              <input
                id="destination-input"
                type="url"
                value={destinationInput}
                onChange={handleInputChange}
                placeholder="https://your-new-destination.com"
                style={{
                  padding: '0.9rem 1rem',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${
                    destinationInput && !validation.valid
                      ? 'rgba(224,92,92,0.5)'
                      : destinationInput && validation.valid
                      ? 'rgba(80,200,120,0.4)'
                      : 'rgba(255,255,255,0.12)'
                  }`,
                  color: COLORS.white,
                  fontSize: '0.88rem',
                  fontFamily: 'monospace',
                  outline: 'none',
                  transition: 'border-color 0.15s',
                }}
              />
              <p style={{ fontSize: '0.68rem', color: COLORS.gray, margin: 0 }}>
                Must be HTTPS. No localhost or private IPs. Max 2048 characters.
              </p>
            </div>

            {/* Validation feedback */}
            {destinationInput && !validation.valid && (
              <ValidationMessage valid={false} errors={validation.errors} />
            )}
            {destinationInput && validation.valid && (
              <ValidationMessage valid={true} errors={['Valid destination URL.']} />
            )}

            {/* Scannability warnings */}
            <ScannabilityWarnings warnings={scannability.warnings} />

            {/* Save error */}
            {saveStatus === 'error' && (
              <p style={{
                fontSize: '0.78rem',
                color: '#e05c5c',
                margin: 0,
                padding: '0.75rem',
                backgroundColor: 'rgba(224,92,92,0.08)',
                border: '1px solid rgba(224,92,92,0.2)',
              }}>
                {saveError}
              </p>
            )}

            <button
              type="submit"
              disabled={!canSave}
              style={{
                padding: '0.95rem',
                backgroundColor: saveStatus === 'saved' ? 'rgba(80,200,120,0.2)' : COLORS.gold,
                color: saveStatus === 'saved' ? '#50c878' : COLORS.black,
                border: saveStatus === 'saved' ? '1px solid #50c878' : 'none',
                fontSize: '0.78rem',
                fontWeight: 800,
                letterSpacing: '0.2em',
                cursor: canSave ? 'pointer' : 'not-allowed',
                opacity: canSave ? 1 : 0.4,
                transition: 'all 0.15s',
              }}
            >
              {saveStatus === 'saving' ? 'UPDATING...' :
               saveStatus === 'saved' ? 'DESTINATION UPDATED' :
               'UPDATE DESTINATION'}
            </button>
          </form>
        </div>
      </div>

      {/* Scan analytics table */}
      <div style={{ marginTop: '1rem' }}>
        <p style={{
          fontSize: '0.65rem',
          letterSpacing: '0.2em',
          color: COLORS.white,
          margin: '0 0 1rem',
          fontWeight: 700,
        }}>
          SCAN ANALYTICS
        </p>
        {analytics ? (
          <div style={{ border: `1px solid rgba(255,255,255,0.08)` }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 80px',
              padding: '0.75rem 1.25rem',
              borderBottom: `1px solid rgba(255,255,255,0.06)`,
              backgroundColor: COLORS.surface,
            }}>
              {['DATE', 'SCANS'].map((h) => (
                <span key={h} style={{ fontSize: '0.6rem', letterSpacing: '0.15em', color: COLORS.gray, fontWeight: 700 }}>
                  {h}
                </span>
              ))}
            </div>
            {analytics.daily.length === 0 ? (
              <div style={{ padding: '1.5rem 1.25rem' }}>
                <p style={{ fontSize: '0.78rem', color: COLORS.gray, margin: 0, fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>
                  No scans in the last 30 days.
                </p>
              </div>
            ) : analytics.daily.map((row) => (
              <div
                key={row.date}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px',
                  padding: '0.85rem 1.25rem',
                  borderBottom: `1px solid rgba(255,255,255,0.04)`,
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: '0.78rem', color: COLORS.white, fontVariantNumeric: 'tabular-nums' }}>
                  {row.date}
                </span>
                <span style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: row.scans > 5 ? COLORS.gold : COLORS.white,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {row.scans}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: '0.72rem', color: COLORS.gray, margin: 0, fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>
            {API_BASE_URL ? 'Loading analytics...' : 'Connect API to view real scan analytics.'}
          </p>
        )}
      </div>
    </div>
  );
}
